import React, { useEffect } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCalendarCheck,
  faVideo,
  faClock,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { useBooking } from "../context/BookingContext";
import { BOOKING_CONFIG } from "../config/booking";

const BookingModal = () => {
  const { isOpen, closeBooking } = useBooking();

  useEffect(() => {
    (async function () {
      try {
        const cal = await getCalApi({ namespace: BOOKING_CONFIG.namespace });
        cal("ui", {
          theme: "dark",
          styles: { branding: { brandColor: BOOKING_CONFIG.brandColor } },
          hideEventTypeDetails: false,
          layout: "month_view",
        });
      } catch (e) {
        console.error("Cal.com initialization error:", e);
      }
    })();
  }, []);

  // Lock body scroll when modal is open and handle ESC key
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") closeBooking();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, closeBooking]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-modal-title"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeBooking}
            className="fixed inset-0 bg-black/75 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="relative w-full max-w-4xl bg-card border border-border/80 rounded-3xl shadow-2xl overflow-hidden z-10 flex flex-col max-h-[92vh] my-auto"
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-5 sm:p-6 border-b border-border bg-secondary/40">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-xl flex-shrink-0">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3
                      id="booking-modal-title"
                      className="text-lg sm:text-xl font-bold text-foreground font-heading tracking-tight"
                    >
                      {BOOKING_CONFIG.title}
                    </h3>
                    <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Instant Confirmation
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground font-medium">
                    <span className="flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faClock} className="text-primary text-[10px]" />
                      30 Mins
                    </span>
                    <span className="text-border">•</span>
                    <span className="flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faVideo} className="text-primary text-[10px]" />
                      Google Meet
                    </span>
                    <span className="text-border">•</span>
                    <span>No commitment</span>
                  </div>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={closeBooking}
                aria-label="Close booking modal"
                className="w-9 h-9 rounded-full bg-secondary/80 hover:bg-secondary text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body / Cal.com Embed */}
            <div className="w-full flex-1 min-h-[520px] sm:min-h-[580px] bg-background relative overflow-y-auto">
              <Cal
                namespace={BOOKING_CONFIG.namespace}
                calLink={BOOKING_CONFIG.calLink}
                style={{ width: "100%", height: "100%", minHeight: "520px", overflow: "auto" }}
                config={{ layout: "month_view", theme: "dark" }}
              />
            </div>

            {/* Modal Footer helper */}
            <div className="p-3.5 sm:p-4 bg-secondary/30 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground text-center sm:text-left">
              <span>
                Prefer email directly? Reach out anytime at{" "}
                <a
                  href="mailto:akashzaheer786@gmail.com"
                  className="text-primary font-medium hover:underline inline-flex items-center gap-1 ml-1"
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-[10px]" />
                  akashzaheer786@gmail.com
                </a>
              </span>
              <button
                onClick={closeBooking}
                className="text-muted-foreground hover:text-foreground font-semibold px-3 py-1 rounded-lg hover:bg-secondary transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
